import type { FC } from "react";
import type { ProductHistoricalInsights } from "../historical-insights.types";
import { formatCurrency, formatDays } from "../insights-utils";

interface Props {
  quoted: ProductHistoricalInsights["quoted"];
}

export const InsightsQuotedSection: FC<Props> = ({ quoted }) => {
  if (quoted.totalCount === 0) {
    return null;
  }

  const classEntries = Object.entries(quoted.byClass);
  const stateEntries = Object.entries(quoted.byState);
  const hasDetailData = classEntries.length > 0 || stateEntries.length > 0 || quoted.topCombinations.length > 0;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Quoted Deals ({quoted.totalCount})</h3>

      {quoted.avgDaysToQuote != null && (
        <p className="text-xs text-muted-foreground">Avg time to quote: {formatDays(quoted.avgDaysToQuote)}</p>
      )}

      {!hasDetailData && <p className="text-xs text-muted-foreground">More data needed for detailed breakdown.</p>}

      {classEntries.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">By Class</h4>
          <div className="space-y-1">
            {classEntries.map(([cls, data]) => (
              <div key={cls} className="flex justify-between text-xs">
                <span>{cls}</span>
                <span className="text-muted-foreground">
                  {data.count} · {formatCurrency(data.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stateEntries.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">By State</h4>
          <div className="space-y-1">
            {stateEntries.map(([state, data]) => (
              <div key={state} className="flex justify-between text-xs">
                <span>{state}</span>
                <span className="text-muted-foreground">
                  {data.count} · {formatCurrency(data.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {quoted.topCombinations.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Combinations</h4>
          <div className="space-y-1">
            {quoted.topCombinations.map((combo, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>
                  {combo.class} / {combo.state}
                </span>
                <span className="text-muted-foreground">
                  {combo.count} · {formatCurrency(combo.avgPremium)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
